import type { ComponentProps } from 'react';
import { CheckIcon, XIcon } from 'lucide-react';
import { FieldInfo, hasFieldError, useFieldContext, useIsFieldOptional } from './shared';
import { FieldLabelRow } from './field-label-row';
import { Input } from '../input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../tooltip';
import { cn } from '../../lib/utils';

type ContrastTextColor = 'white' | 'black';

const EAA_URL = 'https://eur-lex.europa.eu/eli/dir/2019/882/oj';
const WCAG_LEVELS = [
	{ label: 'AA', threshold: 4.5, purpose: 'normal body text', severity: 'required' },
	{ label: 'AAA', threshold: 7, purpose: 'enhanced contrast', severity: 'enhanced' },
] as const;

type Severity = (typeof WCAG_LEVELS)[number]['severity'];

type ColorFieldProps = Omit<ComponentProps<'input'>, 'color'> & {
	label: string;
	/** Text color used for the WCAG contrast hint. Defaults to `'white'`. */
	contrastTextColor?: ContrastTextColor;
};

export default function ColorField({ label, className, contrastTextColor = 'white', ...props }: ColorFieldProps) {
	const field = useFieldContext<string>();
	const isOptional = useIsFieldOptional(field.name);
	const value = field.state.value ?? '';

	return (
		<div className={className}>
			<div className="grid gap-2">
				{label && (
					<FieldLabelRow
						label={label}
						htmlFor={field.name}
						hasError={field.state.meta.isTouched && !field.state.meta.isValid}
						optional={isOptional}
					/>
				)}
				<div className="relative">
					<input
						type="color"
						value={value}
						onBlur={field.handleBlur}
						onChange={(e) => field.handleChange(e.target.value)}
						className="border-input absolute top-1/2 left-3 size-4 -translate-y-1/2 cursor-pointer rounded-full border"
					/>
					<Input
						type="text"
						id={field.name}
						name={field.name}
						aria-invalid={hasFieldError(field.state.meta)}
						value={value}
						onBlur={field.handleBlur}
						onChange={(e) => field.handleChange(e.target.value)}
						className="pl-8"
						{...props}
					/>
				</div>
				<ContrastHint color={value} textColor={contrastTextColor} onApply={(next) => field.handleChange(next)} />
			</div>
			<FieldInfo fieldState={field.state} />
		</div>
	);
}

function WcagContrastExplainer({ textColor }: { textColor: ContrastTextColor }) {
	return (
		<div className="space-y-1.5 text-xs leading-snug">
			<p>
				<strong>Contrast ratio</strong> tells you how readable <strong>{textColor} text</strong> is on top of this
				color. WCAG requires at least <strong>4.5:1</strong> (AA) for text, or <strong>7:1</strong> (AAA) for strict
				compliance.
			</p>
			<p>
				In the EU, the{' '}
				<a
					href={EAA_URL}
					target="_blank"
					rel="noopener noreferrer"
					className="hover:text-foreground underline underline-offset-2"
				>
					European Accessibility Act
				</a>{' '}
				(Directive 2019/882), in force since 28 June 2025, requires most consumer-facing digital products to meet WCAG
				2.1 AA.
			</p>
		</div>
	);
}

function ContrastHint({
	color,
	textColor,
	onApply,
}: {
	color: string;
	textColor: ContrastTextColor;
	onApply: (hex: string) => void;
}) {
	const rgb = parseHexColor(color);
	if (!rgb) return null;

	const textRgb = textColor === 'white' ? { r: 255, g: 255, b: 255 } : { r: 0, g: 0, b: 0 };
	const ratio = contrastRatio(rgb, textRgb);

	return (
		<TooltipProvider>
			<div className="flex items-center gap-3 text-xs">
				<Tooltip>
					<TooltipTrigger asChild>
						<button
							type="button"
							className="inline-flex cursor-help items-center rounded px-1.5 py-0.5 font-medium tabular-nums"
							style={{ backgroundColor: color, color: textColor }}
						>
							Contrast ratio {ratio.toFixed(2)}:1
						</button>
					</TooltipTrigger>
					<TooltipContent className="max-w-xs">
						<WcagContrastExplainer textColor={textColor} />
					</TooltipContent>
				</Tooltip>
				{WCAG_LEVELS.map((level) => (
					<ContrastCheck
						key={level.label}
						label={level.label}
						threshold={level.threshold}
						purpose={level.purpose}
						severity={level.severity}
						ratio={ratio}
						brand={rgb}
						textColor={textColor}
						onApply={onApply}
					/>
				))}
			</div>
		</TooltipProvider>
	);
}

function ContrastCheck({
	label,
	threshold,
	purpose,
	severity,
	ratio,
	brand,
	textColor,
	onApply,
}: {
	label: string;
	threshold: number;
	purpose: string;
	severity: Severity;
	ratio: number;
	brand: Rgb;
	textColor: ContrastTextColor;
	onApply: (hex: string) => void;
}) {
	const passes = ratio >= threshold;
	const suggested = passes ? null : findCompliantColor(brand, textColor, threshold);
	const Icon = passes ? CheckIcon : XIcon;

	const content = (
		<>
			<span className="font-medium">{label}</span>
			<span>{threshold}</span>
			<Icon className="size-3" />
		</>
	);

	const pillClasses = cn(
		'inline-flex items-center gap-1 rounded px-1.5 py-0.5 tabular-nums',
		passes
			? 'bg-emerald-500/10 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-400'
			: severity === 'required'
				? 'bg-destructive/10 text-destructive'
				: 'bg-muted text-muted-foreground',
	);

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<span className={pillClasses}>{content}</span>
			</TooltipTrigger>
			<TooltipContent className="max-w-64">
				<ContrastCheckTooltip
					label={label}
					threshold={threshold}
					purpose={purpose}
					ratio={ratio}
					passes={passes}
					suggested={suggested}
					textColor={textColor}
					onApply={onApply}
				/>
			</TooltipContent>
		</Tooltip>
	);
}

function ContrastCheckTooltip({
	label,
	threshold,
	purpose,
	ratio,
	passes,
	suggested,
	textColor,
	onApply,
}: {
	label: string;
	threshold: number;
	purpose: string;
	ratio: number;
	passes: boolean;
	suggested: string | null;
	textColor: ContrastTextColor;
	onApply: (hex: string) => void;
}) {
	return (
		<div className="space-y-1.5 text-xs leading-snug">
			<p>
				<strong>WCAG {label}</strong> requires at least <strong>{threshold}:1</strong> for {purpose}.
			</p>
			<p>
				Your color reaches <strong>{ratio.toFixed(2)}:1</strong> — {passes ? 'it passes.' : 'it fails.'}
			</p>
			{suggested && (
				<div className="space-y-1 pt-1">
					<p className="text-muted-foreground">Here's a closer shade of your color that meets {label}.</p>
					<button
						type="button"
						onClick={() => onApply(suggested)}
						className="border-border bg-background hover:bg-accent flex w-full items-center gap-2 rounded border px-2 py-1.5 text-left transition-colors"
					>
						<span
							className="border-border inline-block size-4 shrink-0 rounded border"
							style={{ backgroundColor: suggested }}
						/>
						<span className="tabular-nums">{suggested}</span>
						<span className="text-muted-foreground ml-auto">Apply</span>
					</button>
				</div>
			)}
			{!suggested && !passes && (
				<p className="text-muted-foreground">
					No {textColor === 'white' ? 'darker' : 'lighter'} shade of this hue can reach {threshold}:1.
				</p>
			)}
		</div>
	);
}

type Rgb = { r: number; g: number; b: number };

function parseHexColor(value: string): Rgb | null {
	let v = value.trim();
	if (v.startsWith('#')) v = v.slice(1);
	if (v.length === 3) {
		v = v
			.split('')
			.map((c) => c + c)
			.join('');
	}
	if (!/^[0-9a-fA-F]{6}$/.test(v)) return null;
	return {
		r: parseInt(v.slice(0, 2), 16),
		g: parseInt(v.slice(2, 4), 16),
		b: parseInt(v.slice(4, 6), 16),
	};
}

const transform = (c: number) => {
	const s = c / 255;
	return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
};

function relativeLuminance({ r, g, b }: Rgb): number {
	return 0.2126 * transform(r) + 0.7152 * transform(g) + 0.0722 * transform(b);
}

function contrastRatio(a: Rgb, b: Rgb): number {
	const la = relativeLuminance(a);
	const lb = relativeLuminance(b);
	const lighter = Math.max(la, lb);
	const darker = Math.min(la, lb);
	return (lighter + 0.05) / (darker + 0.05);
}

type Hsl = { h: number; s: number; l: number };

function rgbToHsl({ r, g, b }: Rgb): Hsl {
	const R = r / 255;
	const G = g / 255;
	const B = b / 255;
	const max = Math.max(R, G, B);
	const min = Math.min(R, G, B);
	const l = (max + min) / 2;
	let h = 0;
	let s = 0;
	if (max !== min) {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
		if (max === R) h = (G - B) / d + (G < B ? 6 : 0);
		else if (max === G) h = (B - R) / d + 2;
		else h = (R - G) / d + 4;
		h /= 6;
	}
	return { h, s, l };
}

const hue2rgb = (p: number, q: number, t: number) => {
	if (t < 0) t += 1;
	if (t > 1) t -= 1;
	if (t < 1 / 6) return p + (q - p) * 6 * t;
	if (t < 1 / 2) return q;
	if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
	return p;
};

function hslToRgb({ h, s, l }: Hsl): Rgb {
	if (s === 0) {
		const v = Math.round(l * 255);
		return { r: v, g: v, b: v };
	}

	const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
	const p = 2 * l - q;
	return {
		r: Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
		g: Math.round(hue2rgb(p, q, h) * 255),
		b: Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
	};
}

const pad = (n: number) => n.toString(16).padStart(2, '0');

function rgbToHex({ r, g, b }: Rgb): string {
	return `#${pad(r)}${pad(g)}${pad(b)}`;
}

/**
 * Find the shade of the given color (same hue/saturation) that reaches
 * `targetRatio` against the text color, staying as close to the original as
 * possible. Returns null if the color already passes.
 */
function findCompliantColor(brand: Rgb, textColor: ContrastTextColor, targetRatio: number): string | null {
	const textRgb = textColor === 'white' ? { r: 255, g: 255, b: 255 } : { r: 0, g: 0, b: 0 };
	if (contrastRatio(brand, textRgb) >= targetRatio) return null;

	const { h, s, l: originalL } = rgbToHsl(brand);
	// Darken for white text, lighten for black text.
	let goodL = textColor === 'white' ? 0 : 1;
	let badL = originalL;

	for (let i = 0; i < 20; i++) {
		const midL = (goodL + badL) / 2;
		const candidate = hslToRgb({ h, s, l: midL });
		if (contrastRatio(candidate, textRgb) >= targetRatio) {
			goodL = midL;
		} else {
			badL = midL;
		}
	}

	return rgbToHex(hslToRgb({ h, s, l: goodL }));
}
